import bridge from '@/plugins/vue-constructor-bridge';

import returnAppConfig from '@/helpers/return-app-config';

import http from '@/services/http';

export const configureCmpiRequestBody = (orderDetails, { paymentMethod }) => {
  const d = new Date();
  const timezone = d.getTimezoneOffset();
  const dollarsToCents = (parseFloat(orderDetails.total) * 100);

  const body = {
    storeId: orderDetails.store.storeId,
    orderId: orderDetails.orderId,
    type: 'CmpiLookup',
    data: {
      BrowserJavaEnabled: 'False',
      BrowserLanguage: navigator.language,
      BrowserColorDepth: window.screen.colorDepth,
      BrowserScreenHeight: window.screen.height,
      BrowserScreenWidth: window.screen.width,
      BrowserTimeZone: timezone,
      BrowserJavaScriptEnabled: 'True',
      amount: dollarsToCents,
      orderNumber: orderDetails.orderId,
      creditCard: {
        cardType: paymentMethod.cardDetails.cardType.toUpperCase(),
        cardNumber: paymentMethod.cardDetails.cardNumber,
        expirationMonth: paymentMethod.cardDetails.cardExpiration.month,
        expirationYear: paymentMethod.cardDetails.cardExpiration.year,
        securityCode: paymentMethod.cardDetails.cvv
      },
      cardHolder: {
        nameOnCard: paymentMethod.cardDetails.nameOnCard.fullName,
        city: paymentMethod.billingAddress.city,
        state: paymentMethod.billingAddress.state,
        firstStreet: paymentMethod.billingAddress.line1,
        ...paymentMethod.billingAddress.line2 && { secondStreet: paymentMethod.billingAddress.line2 },
        zip: paymentMethod.billingAddress.postalCode
      }
    }
  };

  return body;
};

export const configureTokenRequestBody = (orderDetails) => {
  const dollarsToCents = (parseFloat(orderDetails.total) * 100);
  const body = {
    storeId: orderDetails.store.storeId,
    orderId: orderDetails.orderId,
    type: 'Initialize',
    data: {
      amount: dollarsToCents,
      orderNumber: orderDetails.orderId,
      currency: 'USD'
    }
  };

  return body;
};

export const getToken = async (orderDetails) => {
  const body = configureTokenRequestBody(orderDetails);

  try {
    bridge.triggerNewRelicEvent('CardinalTokenRequested', 'Information', 'Cardinal Token Requested');

    const { data } = await http.post('/cardfree-api/v1/payment/tokenization-key', body);

    bridge.triggerNewRelicEvent('CardinalTokenCompleted', 'Information', 'Cardinal Token Completed');

    return data[0].key;
  }
  catch (error) {
    bridge.triggerNewRelicEvent('CardinalTokenError', 'Error', 'Cardinal Token Error', { errorMessage: error.message });

    return null;
  }
};

/** NOTE: Response payloads come in different formats (ie. Cardinal "payments.validated" callback payload vs. cmpi_lookup payload) */
export const parseResponseBody = (response, isAcsResponse = false) => {
  if (isAcsResponse) {
    return {
      threeDsTransactionId: response.Payment.ProcessorTransactionId,
      programProtocol: response.Payment.ExtendedData.ThreeDsVersion,
      status: response.Payment.ExtendedData.PAResStatus,
      cavv: response.Payment.ExtendedData.CAVV,
      eciIndicator: response.Payment.ExtendedData.ECIFlag
    };
  }

  const {
    threedsServerTransactionId,
    threedsVersion,
    cavv,
    eciFlag,
    paresStatus
  } = response;

  return {
    threeDsTransactionId: threedsServerTransactionId,
    programProtocol: threedsVersion,
    cavv,
    eciIndicator: eciFlag,
    status: paresStatus
  };
};

export const handleChallengedFlow = (cmpiLookUpResponse, successCb, orderDetails, continueOrFailHandler) => {
  const continuePayload = {
    AcsUrl: cmpiLookUpResponse.data.acsUrl,
    Payload: cmpiLookUpResponse.data.payload
  };

  /** https://cardinaldocs.atlassian.net/wiki/spaces/CC/pages/32950/Request+Objects */
  const orderObject = {
    OrderDetails: {
      OrderNumber: orderDetails.orderId,
      Amount: orderDetails.total,
      CurrencyCode: 'USD',
      OrderChannel: 'S',
      TransactionId: cmpiLookUpResponse.data.transactionId
    }
  };

  /** https://cardinaldocs.atlassian.net/wiki/spaces/CC/pages/557065/Songbird.js#Songbird.js-Cardinal.continue */
  window.Cardinal.continue('cca', continuePayload, orderObject);

  /** https://cardinaldocs.atlassian.net/wiki/spaces/CC/pages/557065/Songbird.js#Songbird.js-payments.validated */
  window.Cardinal.on(
    'payments.validated',
    (data) => {
      const isAcsResponse = true;

      switch (data.ActionCode) {
        case 'SUCCESS':
          // eslint-disable-next-line no-case-declarations
          const parsedResponse = parseResponseBody(data, isAcsResponse);

          successCb(parsedResponse);

          bridge.triggerNewRelicEvent('CardinalPaymentsValidated', 'Information', 'Cardinal Payments Validated', { actionCode: data.ActionCode });
          break;

        /** TODO: Should we treat NOACTION as Success? */
        case 'NOACTION':
          bridge.triggerNewRelicEvent('CardinalPaymentsValidated', 'Warning', 'Cardinal Payments Validated', { actionCode: data.ActionCode });

          continueOrFailHandler();
          break;

        case 'FAILURE':
        case 'ERROR':
          bridge.triggerNewRelicEvent('CardinalPaymentsValidated', 'Error', 'Cardinal Payments Validated', { actionCode: data.ActionCode, errorNumber: data.errorNumber });

          continueOrFailHandler();
          break;
        default:
          break;
      }
    }
  );
};

export const handleBinResponse = ({ successCb, cardInfo, orderDetails, continueOrFailHandler }) => async (binResponse) => {
  /** NOTE: successful "binResponse" will return { Status: true } */
  if (binResponse.Status) {
    bridge.triggerNewRelicEvent('CardinalBinDetectionCompleted', 'Information', 'Cardinal Bin Detection Completed');

    try {
      const body = configureCmpiRequestBody(orderDetails, cardInfo);

      bridge.triggerNewRelicEvent('CardinalCmpiRequest', 'Information', 'Cmpi Request Initiated');

      const { data } = await http.post('/cardfree-api/v1/payment/tokenization-key', body);

      const cmpiLookUpResponse = data[0];

      /** NOTE: If cavv, then result is "unchallenged/frictionless" flow. */
      bridge.triggerNewRelicEvent(
        'CardinalCmpiCompleted',
        'Information',
        'Cmpi Request Completed',
        {
          cmpiLookUpResponse: {
            ...cmpiLookUpResponse,
            cavv: 'redacted',
            paymentRedirectUrl: 'redacted',
            data: {
              ...cmpiLookUpResponse.data,
              cavv: 'redacted',
              acsUrl: 'redacted',
              cardBin: 'redacted'
            }
          }
        }
      );

      if (cmpiLookUpResponse.data.cavv) {
        const parsedResponse = parseResponseBody(cmpiLookUpResponse.data);

        successCb(parsedResponse);
      }
      /** NOTE: If no cavv, but acs url, then result is "challenged/friction" flow. */
      else if (cmpiLookUpResponse.data.acsUrl) {
        handleChallengedFlow(cmpiLookUpResponse, successCb, orderDetails, continueOrFailHandler);
      }
      else {
        bridge.triggerNewRelicEvent(
          'CardinalCmpiCompleted',
          'Warning',
          'No cavv or acsUrl ',
          {
            cmpiLookUpResponse: {
              ...cmpiLookUpResponse,
              data: { ...cmpiLookUpResponse.data, cardBin: 'redacted' }
            }
          }
        );

        continueOrFailHandler();
      }
    }
    catch (error) {
      bridge.triggerNewRelicEvent('CardinalCmpiFailure', 'Error', 'Cmpi Request Failure');

      continueOrFailHandler();
    }
  }
  else {
    continueOrFailHandler();
  }
};

export const handleBinDeviceDetection = (orderDetails, continueTransactionOnFail) => (successCb, errorCb, cardInfo) => {
  /** NOTE: Merchants can elect to continue transaction without 3ds authorization (ie.'threeDsByPassOnProviderFailure') */
  const continueOrFailHandler = () => {
    bridge.triggerNewRelicEvent('CardinalContinueOrFailHandler', 'Information', 'Continue or Fail Handler Triggered', { continueTransactionOnFail });

    if (continueTransactionOnFail) {
      successCb();
    }
    else {
      errorCb();
    }
  };

  const binHandlerArgs = {
    successCb,
    cardInfo,
    orderDetails,
    continueOrFailHandler
  };

  bridge.triggerNewRelicEvent('CardinalBinDetectionInitiated', 'Information', 'Cardinal Bin Detection Initiated');

  /** https://cardinaldocs.atlassian.net/wiki/spaces/CC/pages/311984510/BIN+Detection */
  window.Cardinal.trigger('bin.process', cardInfo.paymentMethod.cardDetails.cardNumber)
    .then(handleBinResponse(binHandlerArgs))
    .catch(() => {
      bridge.triggerNewRelicEvent('CardinalBinDetectionFailure', 'Warning', 'Bin Detection Failure');

      continueOrFailHandler();
    });
};

export const initCardinalService = async (orderDetails) => {
  const configuration = {
    logging: {
      level: returnAppConfig().isProductionEnvironment ? 'off' : 'on'
    },
    payment: {
      view: 'modal',
      displayLoading: true,
      displayExitButton: true
    }
  };

  /** https://cardinaldocs.atlassian.net/wiki/spaces/CC/pages/557065/Songbird.js#Songbird.js-Cardinal.configure */
  window.Cardinal.configure(configuration);

  const token = await getToken(orderDetails);

  /** https://cardinaldocs.atlassian.net/wiki/spaces/CC/pages/557065/Songbird.js#Songbird.js-init */
  window.Cardinal.setup('init', { jwt: token });
};
